import React, { useEffect, useState, useCallback } from 'react';
import api, { baixarFicheiro, extrairMensagemErro } from '../services/api';
import { obterPerfilSessao, perfilPode } from '../permissions';
import Paginacao, { MetadadosPaginacao, paginacaoVazia } from '../components/Paginacao';
import PerfilRapidoModal from '../components/PerfilRapidoModal';

type StatusVersaoFactura = 'PENDENTE_VALIDACAO' | 'VALIDADA' | 'INVALIDADA' | 'SUBSTITUIDA';

interface VersaoFactura {
  id: string;
  versao: number;
  nome_original: string;
  mime_type: string;
  status: StatusVersaoFactura;
  motivo_rejeicao: string | null;
  analisado_por?: { id: string; nome: string; email: string } | null;
  data_analise?: string | null;
  data_upload: string;
}

const moeda = (valor: number, codigo = 'KZ') =>
  `${codigo} ${Math.round(Number(valor || 0)).toLocaleString('pt-PT')}`;

const LIMITE_NIVEL4_ILIMITADO = 999_999_999_999_999;

const moedaOuIlimitado = (valor?: number | null, codigo = 'KZ') =>
  Number(valor || 0) >= LIMITE_NIVEL4_ILIMITADO ? `${codigo} ilimitado` : moeda(Number(valor || 0), codigo);

const dataCurta = (data?: string) => data ? new Intl.DateTimeFormat('pt-AO').format(new Date(data)) : 'Sem data';

const lerArquivo = (arquivo?: File | null): Promise<any | null> =>
  new Promise((resolve, reject) => {
    if (!arquivo) return resolve(null);
    const reader = new FileReader();
    reader.onload = () => resolve({ nome: arquivo.name, tipo: arquivo.type, conteudo: String(reader.result || '') });
    reader.onerror = reject;
    reader.readAsDataURL(arquivo);
  });

const arquivoPermitido = (arquivo?: File | null) => {
  if (!arquivo) return false;
  const nome = arquivo.name.toLowerCase();
  return ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'].includes(arquivo.type) ||
    ['.pdf', '.jpg', '.jpeg', '.png'].some((extensao) => nome.endsWith(extensao));
};

const badgeStatusFactura = (status: StatusVersaoFactura) => {
  switch (status) {
    case 'PENDENTE_VALIDACAO':
      return <span className="status-pill status-warning">Pendente de Validação</span>;
    case 'VALIDADA':
      return <span className="status-pill status-done">Validada</span>;
    case 'INVALIDADA':
      return <span className="status-pill status-blocked">Invalidada</span>;
    case 'SUBSTITUIDA':
      return <span className="status-pill muted">Substituída</span>;
    default:
      return <span className="status-pill muted">{status}</span>;
  }
};

const Comprador: React.FC = () => {
  const podeFecharCompra = perfilPode(obterPerfilSessao(), 'comprador.fecharCompra');
  const [requisicoes, setRequisicoes] = useState<any[]>([]);
  const [dashboard, setDashboard] = useState<any>(null);
  const [forms, setForms] = useState<Record<string, any>>({});
  const [detalheProposta, setDetalheProposta] = useState<any | null>(null);
  const [perfilFornecedorId, setPerfilFornecedorId] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [paginacao, setPaginacao] = useState<MetadadosPaginacao>(paginacaoVazia);

  // Estados de gestão de facturas versionadas
  const [historicoFacturas, setHistoricoFacturas] = useState<Record<string, VersaoFactura[]>>({});
  const [modalInvalidar, setModalInvalidar] = useState<{ requisicaoId: string; versaoId: string; versaoNum: number } | null>(null);
  const [motivoInvalidacao, setMotivoInvalidacao] = useState('');

  const carregarHistoricoFacturas = useCallback(async (reqId: string) => {
    try {
      const resp = await api.get(`/requisicoes/${reqId}/facturas/historico`);
      setHistoricoFacturas((prev) => ({ ...prev, [reqId]: resp.data.dados || [] }));
    } catch (err) {
      console.error('Erro ao carregar histórico de facturas:', err);
    }
  }, []);

  const carregar = useCallback(async (pagina = paginacao.pagina) => {
    try {
      const [resposta, painel] = await Promise.all([
        api.get('/requisicoes', { params: { pagina, limite: paginacao.limite } }),
        api.get('/dashboard/bi')
      ]);
      const listaReqs = resposta.data.dados || [];
      setRequisicoes(listaReqs);
      setPaginacao(resposta.data.paginacao || paginacaoVazia);
      setDashboard(painel.data.indicadores?.compras || null);

      // Carregar histórico de facturas para requisições com proposta vencedora ou estados operacionais/concluídos
      for (const req of listaReqs) {
        if (req.proposta_vencedora_id || ['AGUARDANDO_COMPROVATIVO', 'EM_TRANSITO', 'COMPRA_REALIZADA', 'CONCLUIDO'].includes(req.status)) {
          carregarHistoricoFacturas(req.id);
        }
      }
    } catch (erroLista: any) {
      setErro(extrairMensagemErro(erroLista, 'Erro ao carregar fila do comprador.'));
    }
  }, [paginacao.limite, paginacao.pagina, carregarHistoricoFacturas]);

  useEffect(() => {
    carregar(1);
  }, [carregar]);

  const atualizarCampo = (id: string, campo: string, valor: any) => {
    setForms((atuais) => ({ ...atuais, [id]: { ...(atuais[id] || {}), [campo]: valor } }));
  };

  const menorValor = (propostas: any[]) => Math.min(...propostas.map((proposta) => Number(proposta.valor_proposto || 0)));
  const valorExcedeAlcada = (valor: number, limite?: number | null) => Boolean(limite && Number(valor || 0) > limite);

  const definirPrazo = async (id: string) => {
    const dataLimite = forms[id]?.data_limite;
    if (!dataLimite || new Date(dataLimite).getTime() <= Date.now()) {
      setErro('Informe uma data limite futura para receção de propostas.');
      return;
    }

    try {
      setErro(null);
      setMensagem(null);
      const resposta = await api.post(`/requisicoes/${id}/definir-prazo-cotacao`, {
        data_limite: forms[id]?.data_limite
      });
      setMensagem(resposta.data.mensagem);
      carregar();
    } catch (erroPrazo: any) {
      setErro(extrairMensagemErro(erroPrazo, 'Erro ao definir data limite.'));
    }
  };

  const selecionarProposta = async (req: any, proposta: any) => {
    const propostas = req.propostas || [];
    const menor = propostas.length ? menorValor(propostas) : Number(proposta.valor_proposto || 0);
    const justificativa = String(forms[`${req.id}:${proposta.id}`]?.justificativa || '').trim();
    if (Number(proposta.valor_proposto || 0) > menor && !justificativa) {
      setErro('Justificativa obrigatória quando a proposta escolhida não é a de menor valor.');
      return;
    }

    try {
      setErro(null);
      setMensagem(null);
      const resposta = await api.post(`/requisicoes/${req.id}/fechar-com-proposta`, {
        proposta_id: proposta.id,
        justificativa
      });
      setMensagem(resposta.data.mensagem);
      carregar();
    } catch (erroSelecao: any) {
      setErro(extrairMensagemErro(erroSelecao, 'Erro ao selecionar proposta.'));
    }
  };

  const fecharDireta = async (id: string) => {
    const dados = forms[id] || {};
    const valorPago = Number(dados.valor_pago);
    const requisicao = requisicoes.find((item) => item.id === id);
    const limiteAlcada = requisicao?.alcada_financeira_usuario?.limite;
    if (!String(dados.nome_fornecedor_externo || '').trim()) {
      setErro('Informe o fornecedor da compra direta.');
      return;
    }
    if (!Number.isFinite(valorPago) || valorPago <= 0) {
      setErro('Valor de compra deve ser maior que zero.');
      return;
    }
    if (valorExcedeAlcada(valorPago, limiteAlcada)) {
      setErro(`O valor informado ultrapassa a sua alçada financeira atual (${moedaOuIlimitado(limiteAlcada)}).`);
      return;
    }
    if (!String(dados.justificativa_compra_direta || '').trim()) {
      setErro('Informe a justificativa da compra direta.');
      return;
    }
    if (!arquivoPermitido(dados.comprovativo)) {
      setErro('Anexe um comprovativo válido em PDF, JPG, JPEG ou PNG.');
      return;
    }

    try {
      setErro(null);
      setMensagem(null);
      const comprovativo = await lerArquivo(dados.comprovativo);
      const resposta = await api.post(`/requisicoes/${id}/fechar-compra-direta`, {
        nome_fornecedor_externo: String(dados.nome_fornecedor_externo).trim(),
        valor_pago: valorPago,
        justificativa: String(dados.justificativa_compra_direta).trim(),
        comprovativo
      });
      setMensagem(resposta.data.mensagem);
      carregar();
    } catch (erroCompra: any) {
      setErro(extrairMensagemErro(erroCompra, 'Erro ao concluir compra direta.'));
    }
  };

  // Funções de validação e invalidação versionadas
  const validarVersao = async (reqId: string, versaoId: string) => {
    try {
      setErro(null);
      setMensagem(null);
      const resposta = await api.post(`/requisicoes/${reqId}/facturas/${versaoId}/validar`);
      setMensagem(resposta.data.mensagem);
      carregar();
      carregarHistoricoFacturas(reqId);
    } catch (err: any) {
      if (err.response?.status === 409) {
        setErro('Esta factura já foi processada por outro utilizador (concorrência). A atualizar...');
        carregar();
        carregarHistoricoFacturas(reqId);
      } else {
        setErro(extrairMensagemErro(err, 'Erro ao validar factura.'));
      }
    }
  };

  const confirmarInvalidacao = async () => {
    if (!modalInvalidar) return;
    const motivoTrim = motivoInvalidacao.trim();
    if (!motivoTrim) {
      setErro('O motivo da invalidação é obrigatório.');
      return;
    }

    try {
      setErro(null);
      setMensagem(null);
      const resposta = await api.post(`/requisicoes/${modalInvalidar.requisicaoId}/facturas/${modalInvalidar.versaoId}/invalidar`, {
        motivo: motivoTrim
      });
      setMensagem(resposta.data.mensagem);
      setModalInvalidar(null);
      setMotivoInvalidacao('');
      carregar();
      carregarHistoricoFacturas(modalInvalidar.requisicaoId);
    } catch (err: any) {
      if (err.response?.status === 409) {
        setErro('Esta factura já foi processada por outro utilizador (concorrência). A atualizar...');
        setModalInvalidar(null);
        setMotivoInvalidacao('');
        carregar();
        carregarHistoricoFacturas(modalInvalidar.requisicaoId);
      } else {
        setErro(extrairMensagemErro(err, 'Erro ao invalidar factura.'));
      }
    }
  };

  const baixarVersao = async (versaoId: string, nome: string) => {
    try {
      setErro(null);
      await baixarFicheiro(`/facturas/versoes/${versaoId}/download`, nome || 'factura.pdf');
    } catch (err: any) {
      setErro(extrairMensagemErro(err, 'Erro ao baixar documento da factura.'));
    }
  };

  const baixarAnexoProposta = async (proposta: any, tipo: 'documento' | 'comprovativo') => {
    try {
      setErro(null);
      const nome = tipo === 'documento' ? proposta.documento_nome : proposta.comprovativo_nome;
      await baixarFicheiro(`/propostas/${proposta.id}/${tipo}/download`, nome || 'anexo');
    } catch (erroDownload: any) {
      setErro(extrairMensagemErro(erroDownload, 'Erro ao baixar anexo da proposta.'));
    }
  };

  return (
    <div className="workspace-page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Cotação e compra</span>
          <h1>Fluxo do comprador</h1>
          <p>Cotações aprovadas, seleção de propostas e validação documental antes do Armazém.</p>
        </div>
      </header>

      {mensagem && <div className="alerta alerta-sucesso">{mensagem}</div>}
      {erro && <div className="alerta alerta-erro">{erro}</div>}

      <section className="metric-grid">
        <div className="metric-card"><span>Em cotação</span><strong>{dashboard?.em_cotacao || 0}</strong></div>
        <div className="metric-card"><span>Propostas recebidas</span><strong>{dashboard?.propostas_recebidas || 0}</strong></div>
        <div className="metric-card"><span>Aguardando decisão</span><strong>{dashboard?.propostas_aguardando_decisao || 0}</strong></div>
        <div className="metric-card"><span>Compras realizadas</span><strong>{dashboard?.compras_realizadas || 0}</strong></div>
        <div className="metric-card"><span>Aguardando comprovativo</span><strong>{dashboard?.aguardando_comprovativo || 0}</strong></div>
        <div className="metric-card"><span>Enviadas ao armazém</span><strong>{dashboard?.compras_enviadas_armazem || 0}</strong></div>
      </section>

      <section className="card-list">
        {requisicoes.map((req) => {
          const propostas = req.propostas || [];
          const menor = propostas.length ? menorValor(propostas) : 0;
          const emCotacao = req.status === 'EM_COTACAO';
          const alcadaFinanceira = req.alcada_financeira_usuario;
          const valorFinal = Number(req.valor_final_contratado || req.valor_pago_ext || req.proposta_vencedora?.valor_proposto || 0);
          const versoesReq = historicoFacturas[req.id] || [];

          return (
            <article className="buyer-card buyer-card-flow" key={req.id}>
              <div className="buyer-card-main">
                <div className="warehouse-card-header">
                  <span className="status-pill">{req.numero_requisicao}</span>
                  <span className="status-pill muted">{req.status === 'EM_TRANSITO' ? 'EM TRÂNSITO' : req.status}</span>
                </div>
                <h2>{req.titulo}</h2>
                <p>{req.descricao}</p>
                <dl className="meta-grid">
                  <div><dt>Valor financeiro</dt><dd>{valorFinal > 0 ? moeda(valorFinal, req.moeda) : 'A definir por proposta ou compra direta'}</dd></div>
                  <div><dt>Minha alçada</dt><dd>{alcadaFinanceira ? `Nível ${alcadaFinanceira.nivel} · ${moedaOuIlimitado(alcadaFinanceira.limite, req.moeda)}` : 'Não disponível'}</dd></div>
                  <div><dt>Limite propostas</dt><dd>{dataCurta(req.data_vencimento)}</dd></div>
                  <div><dt>Propostas</dt><dd>{propostas.length}</dd></div>
                  <div><dt>Fornecedor vencedor</dt><dd>{req.proposta_vencedora?.fornecedor?.nome_empresa || req.nome_fornecedor_ext || 'Pendente'}</dd></div>
                </dl>

                {emCotacao && (
                  <div className="quote-deadline">
                    <input type="datetime-local" onChange={(e) => atualizarCampo(req.id, 'data_limite', e.target.value)} />
                    <button className="botao" type="button" disabled={!podeFecharCompra} onClick={() => definirPrazo(req.id)}>Definir prazo</button>
                  </div>
                )}

                <div className="proposal-compare">
                  {propostas.map((proposta: any) => {
                    const exigeJustificativa = proposta.valor_proposto > menor;
                    const foraDaAlcada = valorExcedeAlcada(proposta.valor_proposto, alcadaFinanceira?.limite);
                    return (
                      <div className={`proposal-cell ${proposta.status === 'SELECIONADA' ? 'selected' : ''}`} key={proposta.id}>
                        <strong>{proposta.fornecedor?.nome_empresa || 'Fornecedor'}</strong>
                        <span>{moeda(proposta.valor_proposto, proposta.moeda || req.moeda)}</span>
                        <small>{proposta.tempo_entrega_dias} dias · {dataCurta(proposta.data_criacao)}</small>
                        <small>{proposta.status}</small>
                        {foraDaAlcada && <small className="status-blocked">Fora da sua alçada financeira</small>}
                        {proposta.documento_nome ? (
                          <button className="botao" type="button" onClick={() => baixarAnexoProposta(proposta, 'documento')}>
                            Baixar proforma
                          </button>
                        ) : (
                          <small>Sem proforma</small>
                        )}
                        <button type="button" onClick={() => setDetalheProposta(proposta)}>Detalhe</button>
                        {proposta.fornecedor_id && (
                          <button className="botao" type="button" onClick={() => setPerfilFornecedorId(proposta.fornecedor_id)}>
                            Ver perfil
                          </button>
                        )}
                        {emCotacao && podeFecharCompra && (
                          <>
                            {exigeJustificativa && (
                              <textarea
                                placeholder="Justificativa obrigatória"
                                onChange={(e) => atualizarCampo(`${req.id}:${proposta.id}`, 'justificativa', e.target.value)}
                              />
                            )}
                            <button className="botao botao-primario" type="button" disabled={foraDaAlcada} onClick={() => selecionarProposta(req, proposta)}>
                              Selecionar
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}
                  {propostas.length === 0 && <div className="empty-state compact-state">Ainda não há propostas para comparar.</div>}
                </div>

                {/* Seção de Histórico e Validação de Facturas Versionadas */}
                {req.proposta_vencedora_id && (
                  <div className="detail-section" style={{ marginTop: '1.5rem', background: '#f8fafc', padding: '1rem', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                      <h3 style={{ margin: 0, fontSize: '1rem' }}>Histórico de Facturas / Documentos</h3>
                      <button className="botao" type="button" onClick={() => carregarHistoricoFacturas(req.id)}>Atualizar histórico</button>
                    </div>

                    {versoesReq.length === 0 && (
                      <div className="empty-state compact-state">Aguardando submissão de factura pelo fornecedor vencedor.</div>
                    )}

                    <div className="stacked-list">
                      {versoesReq.map((versao) => (
                        <div className="list-row" key={versao.id} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.75rem', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>
                              <strong>Versão {versao.versao}</strong> · {versao.nome_original}
                              <small style={{ display: 'block', color: '#64748b' }}>Enviado em: {dataCurta(versao.data_upload)}</small>
                            </span>
                            <div>{badgeStatusFactura(versao.status)}</div>
                          </div>

                          {versao.motivo_rejeicao && (
                            <div className="alerta alerta-erro" style={{ margin: 0, padding: '0.5rem', fontSize: '0.9rem' }}>
                              <strong>Motivo da invalidação:</strong> {versao.motivo_rejeicao}
                              {versao.analisado_por && <small style={{ display: 'block' }}>Por: {versao.analisado_por.nome} em {dataCurta(versao.data_analise || undefined)}</small>}
                            </div>
                          )}

                          <div className="inline-actions" style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                            <button className="botao" type="button" onClick={() => baixarVersao(versao.id, versao.nome_original)}>
                              Baixar documento
                            </button>
                            {versao.status === 'PENDENTE_VALIDACAO' && podeFecharCompra && req.status !== 'CONCLUIDO' && (
                              <>
                                <button className="botao botao-primario" type="button" onClick={() => validarVersao(req.id, versao.id)}>
                                  Validar Factura
                                </button>
                                <button className="botao" type="button" style={{ background: '#ef4444', color: '#fff', border: 'none' }} onClick={() => setModalInvalidar({ requisicaoId: req.id, versaoId: versao.id, versaoNum: versao.versao })}>
                                  Invalidar Factura
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {emCotacao && podeFecharCompra && (
                <div className="direct-buy-panel">
                  <h3>Compra direta</h3>
                  <input placeholder="Fornecedor" onChange={(e) => atualizarCampo(req.id, 'nome_fornecedor_externo', e.target.value)} />
                  <input placeholder="Valor pago" type="number" onChange={(e) => atualizarCampo(req.id, 'valor_pago', e.target.value)} />
                  <textarea placeholder="Justificativa obrigatória" onChange={(e) => atualizarCampo(req.id, 'justificativa_compra_direta', e.target.value)} />
                  <input accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" type="file" onChange={(e) => atualizarCampo(req.id, 'comprovativo', e.target.files?.[0])} />
                  <button className="botao botao-primario" type="button" onClick={() => fecharDireta(req.id)}>Concluir compra direta</button>
                </div>
              )}
            </article>
          );
        })}
        {requisicoes.length === 0 && <div className="empty-state">Nenhuma requisição aprovada disponível para o comprador.</div>}
        <Paginacao paginacao={paginacao} aoMudarPagina={carregar} />
      </section>

      {/* Modal Obrigatório de Invalidação de Factura */}
      {modalInvalidar && (
        <div className="modal-backdrop" onClick={() => setModalInvalidar(null)}>
          <div className="panel proposal-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h2>Invalidar Factura (Versão {modalInvalidar.versaoNum})</h2>
            <p>Informe o motivo claro da invalidação para que o fornecedor possa submeter uma versão corrigida.</p>
            <textarea
              rows={4}
              placeholder="Descreva o motivo da rejeição (obrigatório)..."
              value={motivoInvalidacao}
              onChange={(e) => setMotivoInvalidacao(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', marginBottom: '1rem' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button className="botao" type="button" onClick={() => { setModalInvalidar(null); setMotivoInvalidacao(''); }}>
                Cancelar
              </button>
              <button
                className="botao"
                type="button"
                style={{ background: '#ef4444', color: '#fff', border: 'none' }}
                disabled={!motivoInvalidacao.trim()}
                onClick={confirmarInvalidacao}
              >
                Confirmar Invalidação
              </button>
            </div>
          </div>
        </div>
      )}

      {detalheProposta && (
        <div className="modal-backdrop" onClick={() => setDetalheProposta(null)}>
          <div className="panel proposal-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{detalheProposta.fornecedor?.nome_empresa || 'Detalhe da proposta'}</h2>
            <dl className="meta-grid">
              <div><dt>Valor</dt><dd>{moeda(detalheProposta.valor_proposto, detalheProposta.moeda)}</dd></div>
              <div><dt>Prazo</dt><dd>{detalheProposta.tempo_entrega_dias} dias</dd></div>
              <div><dt>Submissão</dt><dd>{dataCurta(detalheProposta.data_criacao)}</dd></div>
              <div><dt>Status</dt><dd>{detalheProposta.status}</dd></div>
            </dl>
            <p>{detalheProposta.descricao_tecnica}</p>
            {detalheProposta.documento_nome ? (
              <button className="botao" type="button" onClick={() => baixarAnexoProposta(detalheProposta, 'documento')}>
                Baixar proforma
              </button>
            ) : (
              <small>Sem proforma</small>
            )}
            {detalheProposta.comprovativo_nome && (
              <button className="botao" type="button" onClick={() => baixarAnexoProposta(detalheProposta, 'comprovativo')}>
                Baixar comprovativo de pagamento
              </button>
            )}
            {detalheProposta.fornecedor_id && (
              <button className="botao" type="button" onClick={() => setPerfilFornecedorId(detalheProposta.fornecedor_id)}>
                Ver perfil
              </button>
            )}
            <button className="botao" type="button" onClick={() => setDetalheProposta(null)}>Fechar</button>
          </div>
        </div>
      )}
      <PerfilRapidoModal
        tipo="fornecedor"
        id={perfilFornecedorId}
        aberto={Boolean(perfilFornecedorId)}
        onClose={() => setPerfilFornecedorId(null)}
      />
    </div>
  );
};

export default Comprador;
